// One RTCPeerConnection plus one unreliable data channel per node. The client is
// always the offerer and always creates the data channel with
// {ordered:false, maxRetransmits:0} for UDP-like, exactly-once semantics; the
// node accepts the inbound channel and never creates a reliable one.
//
// Strictly one connection is open at any time (constraint 1: sequential
// probing). The engine opens A, runs, closes, then opens B.

import { type ConnectionSignaling } from './signaling';

/** Factory for an RTCPeerConnection; injectable for tests. */
export type PeerFactory = (config: RTCConfiguration) => RTCPeerConnection;

/** The connection surface the engine depends on. */
export interface ProbeConnection {
  open(): Promise<void>;
  acceptAnswer(sdp: string): Promise<void>;
  addRemoteIce(candidate: RTCIceCandidateInit): Promise<void>;
  send(bytes: Uint8Array): void;
  readonly bufferedAmount: number;
  /** Best-effort address family + endpoints of the selected ICE pair. */
  iceInfo(): Promise<IceInfo>;
  close(): void;
}

/** Result of inspecting the selected ICE candidate pair. */
export interface IceInfo {
  family: 'ipv4' | 'ipv6' | 'unknown';
  localIp?: string;
  remoteIp?: string;
}

/** Construction arguments for a NodeConnection. */
export interface NodeConnectionOptions {
  nodeId: string;
  runId: number;
  signaling: ConnectionSignaling;
  /** Called for every inbound data-channel frame. */
  onFrame: (data: ArrayBuffer) => void;
  iceServers?: RTCIceServer[];
  /** Restrict the connection to one address family, or 'auto' (default). */
  ipMode?: 'auto' | 'ipv4' | 'ipv6';
  peerFactory?: PeerFactory;
  /** How long to wait for the channel to open before failing, in ms. */
  openTimeoutMs?: number;
}

function defaultPeerFactory(config: RTCConfiguration): RTCPeerConnection {
  return new RTCPeerConnection(config);
}

/**
 * A single node's peer connection and probe data channel. The whole open()
 * lifecycle is: create channel, create offer, publish it via signalling, take
 * the answer and remote ICE the engine routes back in, and resolve when the
 * channel reaches "open".
 */
export class NodeConnection implements ProbeConnection {
  private readonly nodeId: string;
  private readonly runId: number;
  private readonly signaling: ConnectionSignaling;
  private readonly onFrame: (data: ArrayBuffer) => void;
  private readonly peerFactory: PeerFactory;
  private readonly iceServers: RTCIceServer[];
  private readonly ipMode: 'auto' | 'ipv4' | 'ipv6';
  private readonly openTimeoutMs: number;

  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private remoteDescriptionSet = false;
  private pendingRemoteIce: RTCIceCandidateInit[] = [];
  private closed = false;

  constructor(opts: NodeConnectionOptions) {
    this.nodeId = opts.nodeId;
    this.runId = opts.runId;
    this.signaling = opts.signaling;
    this.onFrame = opts.onFrame;
    this.peerFactory = opts.peerFactory ?? defaultPeerFactory;
    this.iceServers = opts.iceServers ?? [];
    this.ipMode = opts.ipMode ?? 'auto';
    this.openTimeoutMs = opts.openTimeoutMs ?? 15000;
  }

  open(): Promise<void> {
    const pc = this.peerFactory({ iceServers: this.iceServers });
    this.pc = pc;

    const dc = pc.createDataChannel('probe', {
      ordered: false,
      maxRetransmits: 0,
    });
    dc.binaryType = 'arraybuffer';
    this.dc = dc;

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.signaling.ice(this.nodeId, this.runId, ev.candidate.toJSON());
      }
    };

    dc.onmessage = (ev: MessageEvent) => {
      const data = ev.data;
      if (data instanceof ArrayBuffer) {
        this.onFrame(data);
      }
    };

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(`connection: data channel open timed out for ${this.nodeId}`));
      }, this.openTimeoutMs);

      dc.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      dc.onerror = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(new Error(`connection: data channel error for ${this.nodeId}`));
      };

      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer).then(() => offer))
        .then((offer) => {
          this.signaling.offer(this.nodeId, this.runId, offer.sdp ?? '');
        })
        .catch((err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(err instanceof Error ? err : new Error(String(err)));
        });
    });
  }

  async acceptAnswer(sdp: string): Promise<void> {
    if (!this.pc || this.closed) return;
    await this.pc.setRemoteDescription({ type: 'answer', sdp });
    this.remoteDescriptionSet = true;
    const pending = this.pendingRemoteIce;
    this.pendingRemoteIce = [];
    for (const cand of pending) {
      await this.pc.addIceCandidate(cand);
    }
  }

  async addRemoteIce(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc || this.closed) return;
    // Forced-family mode: drop the node's candidates of the other family so the
    // selected pair (and thus the test) uses only IPv4 or only IPv6.
    if (this.ipMode !== 'auto') {
      const fam = candidateFamily(candidate);
      if (fam !== 'unknown' && fam !== this.ipMode) return;
    }
    // Candidates that arrive before the remote description is set must be
    // queued: addIceCandidate would otherwise reject.
    if (!this.remoteDescriptionSet) {
      this.pendingRemoteIce.push(candidate);
      return;
    }
    await this.pc.addIceCandidate(candidate);
  }

  send(bytes: Uint8Array<ArrayBuffer>): void {
    if (!this.dc || this.dc.readyState !== 'open') return;
    this.dc.send(bytes);
  }

  get bufferedAmount(): number {
    return this.dc?.bufferedAmount ?? 0;
  }

  /**
   * Inspect the selected ICE candidate pair via getStats to learn the address
   * family and endpoints the test actually used. Best-effort; must be called
   * before close(). Returns family "unknown" when stats are unavailable.
   */
  async iceInfo(): Promise<IceInfo> {
    const out: IceInfo = { family: 'unknown' };
    const pc = this.pc;
    if (!pc) return out;
    try {
      const stats = await pc.getStats();
      let pair: RTCIceCandidatePairStats | null = null;
      stats.forEach((r) => {
        const cp = r as RTCIceCandidatePairStats & { nominated?: boolean; bytesSent?: number };
        if (cp.type === 'candidate-pair' && (cp.nominated || cp.state === 'succeeded')) {
          if (!pair || (cp.bytesSent ?? 0) >= ((pair as { bytesSent?: number }).bytesSent ?? 0)) {
            pair = cp;
          }
        }
      });
      if (!pair) return out;
      const p = pair as RTCIceCandidatePairStats;
      const local = p.localCandidateId ? stats.get(p.localCandidateId) : undefined;
      const remote = p.remoteCandidateId ? stats.get(p.remoteCandidateId) : undefined;
      const addr = (c: unknown): string | undefined => {
        const cc = c as { address?: string; ip?: string } | undefined;
        return cc ? (cc.address ?? cc.ip) : undefined;
      };
      out.localIp = addr(local);
      out.remoteIp = addr(remote);
      const fam = (a?: string): IceInfo['family'] | undefined =>
        a ? (a.includes(':') ? 'ipv6' : 'ipv4') : undefined;
      out.family = fam(out.remoteIp) ?? fam(out.localIp) ?? 'unknown';
      return out;
    } catch {
      return out;
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    try {
      this.dc?.close();
    } catch {
      // ignore
    }
    try {
      this.pc?.close();
    } catch {
      // ignore
    }
    this.dc = null;
    this.pc = null;
  }
}

/** Address family of an ICE candidate from its SDP connection-address field. */
function candidateFamily(init: RTCIceCandidateInit): 'ipv4' | 'ipv6' | 'unknown' {
  const addr = (init.candidate ?? '').split(' ')[4];
  if (!addr) return 'unknown';
  if (addr.includes(':')) return 'ipv6';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(addr)) return 'ipv4';
  return 'unknown'; // mDNS/hostname: not filtered
}
